// AppsFlyer Web SDK Event Types
declare global {
  interface Window {
    AF_SMART_SCRIPT?: {
      generateOneLinkURL: (options: any) => { clickURL: string } | null;
      displayQrCode?: (elementId: string, options?: any) => any;
      fireImpressionsLink?: () => void;
      version: string;
    };
    AF?: {
      ANALYTICS: {
        logEvent: (eventName: string, eventParams?: Record<string, any>) => void;
      };
    };
  }
}

// ============================================
// AppsFlyer Event Names - Add new events here
// ============================================
export const AF_EVENTS = {
  // Registration & Onboarding
  COMPLETE_REGISTRATION: 'af_web_CompleteRegistration',
  START_TRIAL: 'af_web_Trial',
  SUBSCRIBE: 'af_web_Subscribe',
  CONTENT_VIEW: 'af_web_ContentView',
  ADD_PAYMENT_INFO: 'af_web_AddPaymentInfo', // Add Payment Info
  BUTTON_CLICK: 'af_web_ButtonClick',
} as const;

export type AFEventName = typeof AF_EVENTS[keyof typeof AF_EVENTS];

// ============================================
// Core Event Firing Function
// ============================================
/**
 * Fire an AppsFlyer web event
 * @param eventName - The event name (use AF_EVENTS constants)
 * @param eventParams - Optional parameters to send with the event
 */
export function fireAppsFlyerEvent(
  eventName: AFEventName | string,
  eventParams?: Record<string, any>
): void {
  try {
    // Check if AppsFlyer Web SDK is available
    if (window.AF?.ANALYTICS?.logEvent) {
      window.AF.ANALYTICS.logEvent(eventName, eventParams);
      console.log(`[AppsFlyer] Event fired via SDK: ${eventName}`, eventParams);
    } else {
      // Fallback: Log the event
      console.log(`[AppsFlyer] Event logged: ${eventName}`, eventParams);
      
      // Fire impressions link if available from SmartScript
      if (window.AF_SMART_SCRIPT?.fireImpressionsLink) {
        window.AF_SMART_SCRIPT.fireImpressionsLink();
        console.log('[AppsFlyer] Fired impressions link');
      }
    }
  } catch (error) {
    console.error('[AppsFlyer] Error firing event:', error);
  }
}

// ============================================
// Pre-built Event Functions - Easy to use
// ============================================

/**
 * Fire CompleteRegistration event
 * Use when: User clicks "Start FREE trial" or completes signup
 */
export function fireCompleteRegistrationEvent(params?: {
  method?: string;
  page?: string;
}): void {
  console.log('[AppsFlyer] Firing af_web_CompleteRegistration event');
  fireAppsFlyerEvent(AF_EVENTS.COMPLETE_REGISTRATION, {
    af_registration_method: params?.method || 'subscription_page',
    af_page: params?.page || 'subscription',
  });
}

/**
 * Fire Trial event
 * Use when: User starts a free trial
 */
export function fireStartTrialEvent(params?: {
  trialDays?: number;
  plan?: string;
}): void {
  console.log('[AppsFlyer] Firing af_web_Trial event');
  fireAppsFlyerEvent(AF_EVENTS.START_TRIAL, {
    af_trial_days: params?.trialDays || 7,
    af_plan: params?.plan || 'free_trial',
  });
}

/**
 * Fire Subscribe event
 * Use when: User completes subscription/payment
 */
export function fireSubscribeEvent(params?: {
  plan?: string;
  price?: number;
  currency?: string;
}): void {
  console.log('[AppsFlyer] Firing af_web_Subscribe event');
  fireAppsFlyerEvent(AF_EVENTS.SUBSCRIBE, {
    af_plan: params?.plan,
    af_price: params?.price,
    af_currency: params?.currency || 'INR',
  });
}

/**
 * Fire AddPaymentInfo event
 * Use when: User adds payment information
 */
export function fireAddPaymentInfoEvent(params?: {
  paymentMethod?: string;
  page?: string;
}): void {
  console.log('[AppsFlyer] Firing af_web_AddPaymentInfo event');
  fireAppsFlyerEvent(AF_EVENTS.ADD_PAYMENT_INFO, {
    af_payment_method: params?.paymentMethod,
    af_page: params?.page || 'payment',
  });
}

/**
 * Fire ContentView event
 * Use when: User views specific content
 */
export function fireContentViewEvent(params?: {
  contentId?: string;
  contentType?: string;
  contentName?: string;
}): void {
  console.log('[AppsFlyer] Firing af_web_ContentView event');
  fireAppsFlyerEvent(AF_EVENTS.CONTENT_VIEW, {
    af_content_id: params?.contentId,
    af_content_type: params?.contentType,
    af_content_name: params?.contentName,
  });
}

/**
 * Fire generic ButtonClick event
 * Use when: Tracking any button click
 */
export function fireButtonClickEvent(params: {
  buttonName: string;
  page: string;
  action?: string;
}): void {
  console.log(`[AppsFlyer] Firing af_web_ButtonClick: ${params.buttonName}`);
  fireAppsFlyerEvent(AF_EVENTS.BUTTON_CLICK, {
    af_button_name: params.buttonName,
    af_page: params.page,
    af_action: params.action,
  });
}

/**
 * Get the AppsFlyer SmartScript generated OneLink URL from sessionStorage
 * 
 * @returns The generated OneLink click URL or null if not available
 */
export function getStoredOneLinkURL(): string | null {
  try {
    const storedURL = sessionStorage.getItem('af_onelink_url');
    if (storedURL) {
      console.log('[AppsFlyer] Retrieved OneLink URL from sessionStorage:', storedURL);
      return storedURL;
    }
    console.log('[AppsFlyer] No OneLink URL found in sessionStorage. Make sure appsFlyerSmartScriptLib() is called first.');
    return null;
  } catch (error) {
    console.error('[AppsFlyer] Error getting stored OneLink URL:', error);
    return null;
  }
}
